// Kit 03 · items 67 to 75 and 85. "Loading is a reading, not a spinner."
//
//   67 text lines    line 12 · radius 4 · row gap 8 · sweep 1400ms · stagger 90ms
//   68 card          71 shimmer
//   69 table rows    70 chart
//   72 generating    74 spinner
//   73 warming field cell 14 · gap 4 · lit teal, unlit 10 percent · fill 12ms per cell
//   75 progress      85 run in progress
//
// keys (67): Not focusable. "Announced as busy, so a screen reader is told the
// region is loading rather than empty." A skeleton with no aria-busy is a
// region that reads as an empty region, which is a different and worse claim
// than "still arriving".
//
// keys (73): "The count is announced on completion, not on every cell." A live
// region that fired on each of a hundred cells would say the number a hundred
// times, which is how an accessible feature becomes the reason someone turns
// the screen reader off.

import type { ReactNode } from "react";
import { LoaderCircle } from "lucide-react";
import { Icon } from "./icon";
import { cx } from "./kit-state";
import type { Stateful } from "./kit-state";

/* ═══ 67 to 71 · skeletons ══════════════════════════════════════════════════
   The sweep and the per-row stagger are --d-sweep and --d-stagger, read off
   F8_motion.named.shimmer, which is where the specimen's 1400ms and 90ms live.
   The stagger is applied as an inline animation-delay computed from the row
   index rather than as N hand-written classes, so a fourth line staggers
   correctly without anyone adding a rule.
   ═════════════════════════════════════════════════════════════════════════ */

/** One shimmering bar. `width` is a percentage of the container; `tall` gives
    the card and chart blocks their height without a second component. */
export function Skeleton({
  width, row = 0, block, className,
}: {
  /** 0 to 1. Absent means full width. */
  width?: number;
  /** Which row this is, for the stagger. */
  row?: number;
  /** "value" is the 32px figure in a metric card; "chart" is the 96px plot. */
  block?: "value" | "chart";
  className?: string;
}) {
  return (
    <span
      className={cx("k-skel", block && `k-skel--${block}`, className)}
      style={{
        width: width === undefined ? undefined : `${width * 100}%`,
        animationDelay: `calc(var(--d-stagger) * ${row})`,
      }}
    />
  );
}

/**
 * 67 · a paragraph of text lines.
 *
 * The widths taper because a real paragraph does. They are the specimen's:
 * 100, 82, 64. `lines` picks how many, and the widths repeat from that list
 * rather than being invented for line four.
 */
const LINE_WIDTHS = [1, 0.82, 0.64];

export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cx("k-skel-stack", className)} aria-busy="true" aria-live="off">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} row={i} width={LINE_WIDTHS[i % LINE_WIDTHS.length]} />
      ))}
    </div>
  );
}

/** 68 · a metric card while it loads: a label bar and the figure beneath it. */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cx("k-skel-card", className)} aria-busy="true">
      <Skeleton width={0.4} row={0} />
      <Skeleton width={0.56} row={1} block="value" />
    </div>
  );
}

/**
 * 69 · table rows. The column template is the caller's, because a skeleton
 * whose columns do not line up with the table it stands in announces itself as
 * a skeleton twice.
 */
export function SkeletonTable({
  rows = 2, columns = 3, className,
}: { rows?: number; columns?: number; className?: string }) {
  return (
    <div className={cx("k-skel-stack", className)} aria-busy="true">
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="k-skel-row" style={{ ["--k-skel-cols" as string]: String(columns) }}>
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} row={r + c} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** 70 · a chart block. */
export function SkeletonChart({ className }: { className?: string }) {
  return (
    <div className={cx("k-skel-stack", className)} aria-busy="true">
      <Skeleton row={2} block="chart" />
    </div>
  );
}

/* ═══ 74 · spinner ══════════════════════════════════════════════════════════
   F8 $never: "Spinners on anything under one second." The period is --d-spin.

   Kit 03 item 72 draws its spinner at 900ms inline. --d-spin is 1000ms, ruled
   and left alone; the disagreement is on the books as design/TICKETS.md
   UPM-T8. One value, one place, and the place is tokens.json.
   ═════════════════════════════════════════════════════════════════════════ */

export function Spinner({ label, className }: { label?: string; className?: string }) {
  return (
    <span className={cx("k-spin", className)}>
      <Icon icon={LoaderCircle} label={label} />
    </span>
  );
}

/**
 * 72 · generating. A spinner and the sentence saying what is being generated.
 * The sentence is required: a spinner with no subject is the state this whole
 * section of the kit exists to replace.
 */
export function Generating({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx("k-generating", className)} role="status" aria-live="polite">
      <Spinner />
      <span className="k-generating__label">{children}</span>
    </div>
  );
}

/* ═══ 75 · progress ═════════════════════════════════════════════════════════ */

export interface ProgressProps extends Stateful {
  /** 0 to 1. */
  value: number;
  /** The trailing clause, e.g. "about 20 seconds left". The percentage in
      front of it is DERIVED from `value` and is never passed in: the specimen's
      "62 percent" beside a 62% fill is precisely the pair the kits warn about. */
  note?: string;
  label: string;
  className?: string;
}

export function Progress({ value, note, label, className }: ProgressProps) {
  const pct = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <div className={cx("k-progress", className)}>
      <div
        className="k-progress__track"
        role="progressbar"
        aria-label={label}
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span className="k-progress__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="k-meta" data-num="true">
        {pct} percent{note ? ` · ${note}` : ""}
      </span>
    </div>
  );
}

/* ═══ 73, 85 · the warming field ════════════════════════════════════════════
   redline: cell 14 · gap 4 · lit teal, unlit 10 percent · fill 12ms per cell
   keys: Not focusable. The count is announced on completion, not on every cell.

   "Cells light as models respond, so the loading state IS the result." Which
   means the count and the grid are the same number seen twice, and the one way
   to guarantee they agree is to have one number. `lit` is the only input; the
   caption reads it, the grid reads it, and neither is written down.

   F13's field is a 10x10 grid where one cell is one percentage point, so the
   denominator is fixed at 100 and is not a prop. A 40-cell field cannot render
   31 percent — that is the kits' own second-commonest defect, and the fix is a
   denominator chosen by the value rather than by the layout.
   ═════════════════════════════════════════════════════════════════════════ */

const FIELD_TOTAL = 100;

export interface WarmingFieldProps {
  /** How many of the hundred have come back. */
  lit: number;
  /** The sentence above the grid. */
  title: string;
  /** What the wait is for. Item 73 explains the 15 to 45 seconds. */
  children?: ReactNode;
  className?: string;
}

export function WarmingField({ lit, title, children, className }: WarmingFieldProps) {
  const done = Math.round(Math.min(FIELD_TOTAL, Math.max(0, lit)));
  const complete = done === FIELD_TOTAL;

  return (
    <div className={cx("k-warm", className)} aria-busy={!complete}>
      <div className="k-warm__head">
        <span className="k-warm__title">{title}</span>
        {/* One live region, and it only speaks when there is nothing left to
            say. Until then the region is busy and the count is silent. */}
        <span className="k-meta" data-num="true" aria-live={complete ? "polite" : "off"}>
          {done} of {FIELD_TOTAL}
        </span>
      </div>
      <div className="k-warm__grid" aria-hidden="true">
        {Array.from({ length: FIELD_TOTAL }, (_, i) => (
          <span
            key={i}
            className="k-warm__cell"
            data-lit={i < done ? "true" : undefined}
            style={{ transitionDelay: `calc(var(--d-cell) * ${i})` }}
          />
        ))}
      </div>
      {children ? <p className="k-warm__note">{children}</p> : null}
    </div>
  );
}
